import { DateTime } from 'luxon';
import type { SyncedTripLeg, TripLeg } from '../data/models/reservation.model';
import { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import { TripLegExceptionLogEntry } from '../data/models/trip-leg.model';
import { TripLegConfidenceRequest } from '../data/requests/trip-leg-confidence';

class TripLegUtils {
  public formatDistance({
    distance,
    precision = 1,
    unitPlural = 'miles',
    unitSingular = 'mile',
  }: {
    distance: number;
    precision?: number;
    unitPlural?: string;
    unitSingular?: string;
  }): string {
    if (distance === 1) {
      return `${Number.parseFloat(distance.toFixed(precision))} ${unitSingular}`;
    }
    return `${Number.parseFloat(distance.toFixed(precision))} ${unitPlural}`;
  }

  public getExceptionTranslationString(exceptionCode: string): string | null {
    if (!exceptionCode) return null;
    switch (exceptionCode.toUpperCase()) {
      case 'DRIVERLATE':
        return 'tripLegException.driverLate';
      case 'NODRIVER':
        return 'tripLegException.noDriver';
      case 'DRIVERNOTONLINE':
        return 'tripLegException.driverNotOnline';
      case 'OPENSAMEDAY':
        return 'tripLegException.open';
      default:
        return null;
    }
  }

  public getLatestExceptionAssignmentLog(
    tripLeg: TripLeg,
  ): TripLegExceptionLogEntry | null {
    if (
      tripLeg.tripLegExceptionLogs &&
      tripLeg.tripLegExceptionLogs?.length > 0
    ) {
      const sortedLogs = [...tripLeg.tripLegExceptionLogs]
        .filter(
          (log) =>
            log.assignedToUser !== null && log.assignedToUser !== undefined,
        )
        .sort(
          (a, b) =>
            DateTime.fromISO(b.createdOnUTC).toMillis() -
            DateTime.fromISO(a.createdOnUTC).toMillis(),
        );
      return sortedLogs[0];
    }
    return null;
  }

  public getExceptionAssignmentUserName(tripLeg: TripLeg): string | null {
    if (
      tripLeg.tripLegExceptionLogs &&
      tripLeg.tripLegExceptionLogs?.length > 0
    ) {
      const mostRecentAssignmentLog =
        this.getLatestExceptionAssignmentLog(tripLeg);
      if (
        mostRecentAssignmentLog?.assignedToUser?.userFirstName &&
        mostRecentAssignmentLog?.assignedToUser?.userLastName
      ) {
        return `${mostRecentAssignmentLog?.assignedToUser?.userFirstName} ${mostRecentAssignmentLog?.assignedToUser?.userLastName}`;
      }
    }
    return null;
  }

  public getExceptionAssignmentUserID(tripLeg: TripLeg): number | null {
    if (
      tripLeg.tripLegExceptionLogs &&
      tripLeg.tripLegExceptionLogs?.length > 0
    ) {
      const mostRecentAssignmentLog =
        this.getLatestExceptionAssignmentLog(tripLeg);
      if (mostRecentAssignmentLog?.assignedToUser?.userAccountID) {
        return mostRecentAssignmentLog?.assignedToUser?.userAccountID;
      }
    }
    return null;
  }

  public canReopenTripLeg(tripLeg: TripLeg): boolean {
    return Boolean(
      tripLeg.tripLegRejectedReasons &&
      tripLeg.tripLegStatusBC === TripLegStatusBC.Open,
    );
  }

  public getDefaultTripEventDate(tripTimeZone?: string): DateTime {
    if (!tripTimeZone) {
      return DateTime.now();
    }

    const nowInTripTimeZone = DateTime.now().setZone(tripTimeZone);
    return nowInTripTimeZone.isValid ? nowInTripTimeZone : DateTime.now();
  }

  private resolveTripEventDateTime(
    eventDate: DateTime | null | undefined,
    tripTimeZone?: string,
  ): DateTime | null {
    if (!eventDate?.isValid) {
      return null;
    }

    if (!tripTimeZone) {
      return eventDate;
    }

    const tripDateTime = eventDate.setZone(tripTimeZone, {
      keepLocalTime: true,
    });

    return tripDateTime.isValid ? tripDateTime : eventDate;
  }

  public toTripEventDateTimeUTCISO(
    eventDate: DateTime | null | undefined,
    tripTimeZone?: string,
  ): string | null {
    const resolvedDateTime = this.resolveTripEventDateTime(
      eventDate,
      tripTimeZone,
    );
    return resolvedDateTime?.toUTC().toISO() ?? null;
  }

  public getTimeSinceLastLog(tripLegLogs: any[]): string {
    if (!tripLegLogs || tripLegLogs.length === 0) {
      return '';
    }
    const sortedLogs = [...tripLegLogs].sort(
      (a, b) =>
        DateTime.fromISO(a.createdOnUTC).toMillis() -
        DateTime.fromISO(b.createdOnUTC).toMillis(),
    );
    const startIndex = sortedLogs.findIndex(
      (e) =>
        e.tripLegExceptionID !== null && e.tripLegExceptionID !== undefined,
    );
    const createdOnUTC = sortedLogs[startIndex]?.createdOnUTC;
    if (!createdOnUTC) {
      return '';
    }
    const createdTime = DateTime.fromISO(createdOnUTC);
    if (!createdTime.isValid) {
      return '';
    }
    const now = DateTime.now();
    const durationMillis = Math.abs(now.toMillis() - createdTime.toMillis());
    const totalMinutes = Math.floor(durationMillis / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  }

  public getTripLegF1PayloadFromTransportResponse(
    tripLeg: SyncedTripLeg,
  ): TripLeg {
    const newTripLeg = {} as TripLeg;
    newTripLeg.passengerUUID = tripLeg.passengerUUID!;
    newTripLeg.reservationReferenceID = tripLeg.reservationReferenceID;
    newTripLeg.tripLegSequence = tripLeg.tripLegSequence;
    newTripLeg.tripLegReferenceID = tripLeg.tripLegReferenceID;
    newTripLeg.appointmentDate = tripLeg.appointmentDate;
    newTripLeg.clientBC = tripLeg.clientBC;
    newTripLeg.state = tripLeg.state;
    newTripLeg.status = tripLeg.status;
    // newTripLeg.tripLegStatusReasonBCs = [tripLeg.tripLegStatusReasonBC];
    // newTripLeg.tripLegStatusDateTimeUTC = tripLeg.tripLegStatusDateTimeUTC;
    newTripLeg.pickUpAddress = {
      addressLine1: tripLeg.pickUpAddressLine1 || '',
      addressLine2: tripLeg.pickUpAddressLine2 || '',
      city: tripLeg.pickUpCity || '',
      stateCode: tripLeg.pickUpStateCode || '',
      zipCode: tripLeg.pickUpZipCode || '',
    };
    newTripLeg.dropOffAddress = {
      addressLine1: tripLeg.dropOffAddressLine1 || '',
      addressLine2: tripLeg.dropOffAddressLine2 || '',
      city: tripLeg.dropOffCity || '',
      stateCode: tripLeg.dropOffStateCode || '',
      zipCode: tripLeg.dropOffZipCode || '',
    };
    newTripLeg.overrides = [];
    return newTripLeg;
  }

  public getTripLegConfidencePayload(
    tripLeg: Partial<TripLeg>,
  ): TripLegConfidenceRequest {
    const confidencePayload = {} as TripLegConfidenceRequest;
    confidencePayload.pickUpAddressLine1 =
      tripLeg.pickUpAddress?.addressLine1 || '';
    confidencePayload.pickUpAddressLine2 =
      tripLeg.pickUpAddress?.addressLine2 || '';
    confidencePayload.pickUpCity = tripLeg.pickUpAddress?.city || '';
    confidencePayload.pickUpStateCode = tripLeg.pickUpAddress?.stateCode || '';
    confidencePayload.pickUpZipCode = tripLeg.pickUpAddress?.zipCode || '';
    confidencePayload.dropOffAddressLine1 =
      tripLeg.dropOffAddress?.addressLine1 || '';
    confidencePayload.dropOffAddressLine2 =
      tripLeg.dropOffAddress?.addressLine2 || '';
    confidencePayload.dropOffCity = tripLeg.dropOffAddress?.city || '';
    confidencePayload.dropOffStateCode =
      tripLeg.dropOffAddress?.stateCode || '';
    confidencePayload.dropOffZipCode = tripLeg.dropOffAddress?.zipCode || '';
    return confidencePayload;
  }

  public upsertTripLegData(tripLeg: TripLeg, newTripLegData: TripLeg): TripLeg {
    const newTripLeg = { ...tripLeg, ...newTripLegData } as TripLeg;
    if (newTripLegData.dropOffAddress) {
      newTripLeg.tripLeg!.dropOffAddressLine1 =
        newTripLegData.dropOffAddress.addressLine1 || '';
      newTripLeg.tripLeg!.dropOffAddressLine2 =
        newTripLegData.dropOffAddress.addressLine2 || '';
      newTripLeg.tripLeg!.dropOffCity =
        newTripLegData.dropOffAddress.city || '';
      newTripLeg.tripLeg!.dropOffStateCode =
        newTripLegData.dropOffAddress.stateCode || '';
      newTripLeg.tripLeg!.dropOffZipCode =
        newTripLegData.dropOffAddress.zipCode || '';
    }
    if (newTripLegData.pickUpAddress) {
      newTripLeg.tripLeg!.pickUpAddressLine1 =
        newTripLegData.pickUpAddress.addressLine1 || '';
      newTripLeg.tripLeg!.pickUpAddressLine2 =
        newTripLegData.pickUpAddress.addressLine2 || '';
      newTripLeg.tripLeg!.pickUpCity = newTripLegData.pickUpAddress.city || '';
      newTripLeg.tripLeg!.pickUpStateCode =
        newTripLegData.pickUpAddress.stateCode || '';
      newTripLeg.tripLeg!.pickUpZipCode =
        newTripLegData.pickUpAddress.zipCode || '';
    }
    if (newTripLegData.appointmentDate) {
      newTripLeg.appointmentDate = newTripLegData.appointmentDate;
    }
    if (newTripLegData.appointmentDateTime) {
      newTripLeg.appointmentDateTime = newTripLegData.appointmentDateTime;
    }
    if (newTripLegData.pickUpDueDateTime) {
      newTripLeg.pickUpDueTimeUTC = newTripLegData.pickUpDueDateTime;
      if (newTripLeg.tripLeg) {
        newTripLeg.tripLeg.pickUpDueTimeUTC = newTripLegData.pickUpDueDateTime;
      }
    }
    if (newTripLegData.pickUpDueTimeUTC) {
      newTripLeg.pickUpDueTimeUTC = newTripLegData.pickUpDueTimeUTC;
    }
    newTripLeg.passengerCareLevelBC =
      newTripLegData.passengerCareLevelBC || undefined;
    newTripLeg.passengerCareLevelName =
      newTripLegData.passengerCareLevelName || undefined;
    newTripLeg.transportationProviders =
      newTripLegData.transportationProviders || [];
    if (newTripLegData.transportationProvider) {
      newTripLeg.transportationProvider = newTripLegData.transportationProvider;
    }
    if (newTripLegData.transportationAttributes) {
      newTripLeg.transportationAttributes = [
        ...newTripLegData.transportationAttributes,
      ];
    }
    if (newTripLegData.tripModeBC) {
      newTripLeg.tripModeBC = newTripLegData.tripModeBC;
      if (newTripLegData.tripModeName) {
        newTripLeg.tripModeName = newTripLeg.tripModeBC;
      } else {
        newTripLeg.tripModeName = undefined;
      }
    } else {
      newTripLeg.tripModeBC = undefined;
      newTripLeg.tripModeName = undefined;
    }
    if (newTripLegData.status) {
      newTripLeg.tripLegStatusBC = newTripLegData.status.toLocaleUpperCase();
      newTripLeg.tripLegStatusName =
        newTripLegData.status[0].toLocaleUpperCase() +
        newTripLegData.status.substring(1).toLocaleLowerCase();
    }
    return newTripLeg;
  }
}

const tripLegUtils = new TripLegUtils();

export default tripLegUtils;
